/**
 * Customer ledger sort comparators + 3-click cycle (SCRUM-206), ported from
 * Ryan's prototype. The default sort is "recent" (most recent activity first).
 */
import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { Product } from '@/features/product-scan/product-scan.schema';

import { INSIGHT_ORDER } from '../campaigns-config';
import { customerHolderAge } from '../customer-derived';
import { customerEligibility } from '../customer-eligibility';
import type { SortDirection } from '../campaigns.types';

/** Sortable ledger columns. `recent` is the implicit default (no header click). */
export type SortColumn =
  | 'recent'
  | 'name'
  | 'type'
  | 'holderAge'
  | 'age'
  | 'balance'
  | 'holdings'
  | 'signals'
  // SCRUM-977 — Ryan sorts the Match column by best-eligibility ratio.
  | 'match'
  // SCRUM-956 — geography. Text (name-like) sort, so NOT numeric.
  | 'branch'
  | 'region'
  | 'last';

/**
 * Columns Ryan treats as NUMERIC — first click sorts descending, and the sort
 * menu shows "low → high / high → low" wording (vs "A → Z / Z → A" for text).
 * Transcribed from 7b.html `renderColumnMenu`/`tableSetSort` numeric set:
 * ['age','holderAge','balance','signals','match','last'].
 */
export const NUMERIC_COLUMNS: readonly SortColumn[] = [
  'holderAge',
  'age',
  'balance',
  'signals',
  'match',
  'last',
];

/** Ryan's exact sort-menu labels for a column (text vs numeric wording). */
export function sortLabels(column: SortColumn): { asc: string; desc: string } {
  const numeric = NUMERIC_COLUMNS.includes(column);
  return numeric
    ? { asc: 'Sort low → high', desc: 'Sort high → low' }
    : { asc: 'Sort A → Z', desc: 'Sort Z → A' };
}

const MAX_SAFE = Number.MAX_SAFE_INTEGER;

/** Number of currently-active insight flags on a customer. */
function activeInsightCount(c: Customer): number {
  return INSIGHT_ORDER.reduce((n, k) => (c.insights[k]?.active ? n + 1 : n), 0);
}

/**
 * Recent-activity score: the smaller of the freshest active-insight age and the
 * last-campaign age. Lower = more recent. Customers with no activity score
 * Infinity and sort last.
 */
export function recentActivityScore(c: Customer): number {
  let minInsightAge = Infinity;
  for (const k of INSIGHT_ORDER) {
    const flag = c.insights[k];
    if (flag?.active && flag.age < minInsightAge) minInsightAge = flag.age;
  }
  const campaignAge = c.lastCampaign ? c.lastCampaign.daysAgo : Infinity;
  return Math.min(minInsightAge, campaignAge);
}

/** Best eligibility ratio across the active products (Ryan's `bestMatchRatio`). */
function bestMatchRatio(c: Customer, products: readonly Product[]): number {
  let best = 0;
  for (const p of products) {
    const e = customerEligibility(c, p);
    if (e.total > 0) {
      const r = e.met / e.total;
      if (r > best) best = r;
    }
  }
  return best;
}

/**
 * Builds the comparator for the given column + direction. `products` is only
 * consulted by the `match` column (Ryan sorts it by best-eligibility ratio); it
 * defaults to empty so every other call site is unaffected.
 */
export function getSortComparator(
  column: SortColumn,
  direction: SortDirection,
  products: readonly Product[] = [],
): (a: Customer, b: Customer) => number {
  const dir = direction === 'asc' ? 1 : -1;

  switch (column) {
    case 'recent':
      return (a, b) => {
        const sa = recentActivityScore(a);
        const sb = recentActivityScore(b);
        if (sa === Infinity && sb === Infinity) return 0;
        if (sa === Infinity) return 1;
        if (sb === Infinity) return -1;
        return sa - sb;
      };
    case 'name':
      return (a, b) => a.name.localeCompare(b.name) * dir;
    case 'type':
      return (a, b) => {
        const c = a.accountType.localeCompare(b.accountType);
        return (c !== 0 ? c : a.name.localeCompare(b.name)) * dir;
      };
    case 'holderAge':
      return (a, b) => {
        const aa = customerHolderAge(a);
        const ab = customerHolderAge(b);
        if (aa == null && ab == null) return 0;
        if (aa == null) return 1;
        if (ab == null) return -1;
        return (aa - ab) * dir;
      };
    case 'age':
      return (a, b) => (a.ageMo - b.ageMo) * dir;
    case 'balance':
      return (a, b) => (a.balance - b.balance) * dir;
    case 'branch':
      return (a, b) => a.branch.localeCompare(b.branch) * dir;
    case 'region':
      return (a, b) => a.region.localeCompare(b.region) * dir;
    case 'holdings':
      return (a, b) => (a.holdings.length - b.holdings.length) * dir;
    case 'signals':
      return (a, b) => (activeInsightCount(a) - activeInsightCount(b)) * dir;
    case 'match':
      return (a, b) => (bestMatchRatio(a, products) - bestMatchRatio(b, products)) * dir;
    case 'last':
      return (a, b) => {
        const la = a.lastCampaign ? a.lastCampaign.daysAgo : MAX_SAFE;
        const lb = b.lastCampaign ? b.lastCampaign.daysAgo : MAX_SAFE;
        if (la === MAX_SAFE && lb === MAX_SAFE) return 0;
        if (la === MAX_SAFE) return 1;
        if (lb === MAX_SAFE) return -1;
        return (la - lb) * dir;
      };
  }
}

export interface SortState {
  column: SortColumn;
  direction: SortDirection;
}

export const DEFAULT_SORT: SortState = { column: 'recent', direction: 'asc' };
